import { ModelCardInterface } from "@dashart/dashart-gpt-shared-library";
import { ModelCardServiceInterface } from "../interfaces/ModelCardServiceInterface";
import { BaseService } from "./BaseService";

/**
 * Service class for interacting with Model Card APIs.
 * Provides methods to fetch models from all providers or specific providers.
 */
export class ModelCardService extends BaseService implements ModelCardServiceInterface {

    /** Singleton instance of ModelCardService */
    private static _instance: ModelCardService;

    constructor() {
        super();
    }

    protected getConfiguration(): { defaultModel: string; apiResourceName: string; } {
        return {
            defaultModel: '',
            apiResourceName: 'model-cards',
        }
    }

    /**
     * Gets the singleton instance of ModelCardService.
     * @returns Singleton instance of ModelCardService.
     */
    public static getInstance(): ModelCardService {
        return this._instance ?? (this._instance = new ModelCardService());
    }

    /**
     * Retrieves all models from all providers.
     * @returns Promise resolving to a grouped object of models by provider.
     */
    public async getAllModels(): Promise<{
        openai: ModelCardInterface[],
        claude: ModelCardInterface[],
        mistral: ModelCardInterface[],
        gemini: ModelCardInterface[],
        openrouter: ModelCardInterface[]
    }> {
        const response = await this.axiosInstance.get(
            `/v1/${this.getConfiguration().apiResourceName}/`,
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        return response.data;
    }

    /**
     * Retrieves models for a specific provider.
     * @param provider The provider name (openai, claude, mistral, gemini, openrouter)
     * @returns Promise resolving to an object with provider name and array of models.
     */
    public async getModelsByProvider(provider: 'openai' | 'claude' | 'mistral' | 'gemini' | 'openrouter'): Promise<{
        provider: string,
        models: ModelCardInterface[]
    }> {
        const response = await this.axiosInstance.get(
            `/v1/${this.getConfiguration().apiResourceName}/provider/${provider}/`,
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        return response.data;
    }

    /**
     * Retrieves a specific model by ID.
     * @param id The ID of the model to retrieve.
     * @returns Promise resolving to a ModelCardInterface.
     */
    public async getModelById(id: string): Promise<ModelCardInterface> {
        const response = await this.axiosInstance.get(
            `/v1/${this.getConfiguration().apiResourceName}/model/${id}/`,
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        return response.data;
    }

    /**
     * Refreshes the model cards cache on the server.
     * @returns Promise resolving to a confirmation message.
     */
    public async refreshCache(): Promise<{ message: string }> {
        const response = await this.axiosInstance.post(
            `/v1/${this.getConfiguration().apiResourceName}/refresh-cache/`,
            {},
            { headers: this.getDefaultHeaders() }
        );
        await this.checkResponseStatus(response);
        return response.data;
    }
}